import React, { useEffect, useState } from 'react';
import { X, Check, Send, AlertCircle, Calendar, Truck, ShoppingBag, Clock } from 'lucide-react';
import { Button } from './ui/Button';
export type ServiceType = 'foretagsbestallningar' | 'tartbestallning' | 'catering';
interface OrderFormProps {
  type: ServiceType;
  onClose: () => void;
}
const serviceConfig: Record<ServiceType, {
  title: string;
  subtitle: string;
  emoji: string;
}> = {
  foretagsbestallningar: {
    title: 'Företagsbeställningar',
    subtitle: 'Frukostpaket, luncher & fika till kontoret',
    emoji: '🏢'
  },
  tartbestallning: {
    title: 'Tårtbeställning',
    subtitle: 'Skräddarsydda tårtor för alla tillfällen',
    emoji: '🎂'
  },
  catering: {
    title: 'Catering',
    subtitle: 'Mat & fika till fest och event',
    emoji: '🥂'
  }
};
const WEB3FORMS_KEY = '00888808-a120-4a58-b457-d0746e22e1c7';
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const textareaClass = 'w-full min-h-[100px] p-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans resize-y text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const required = <span className="text-red-500 ml-0.5" data-id="element-176">*</span>;
export function OrderForm({
  type,
  onClose
}: OrderFormProps) {
  const config = serviceConfig[type];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    deliveryType: 'Avhämtning',
    orderType: 'Engångsorder',
    details: '' // Frukostpaket, luncher, etc.
  });
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const payload = {
      access_key: WEB3FORMS_KEY,
      subject: `Ny beställning: ${config.title} – ${formData.name}`,
      from_name: 'Top Notch Bakery Beställningar',
      replyto: formData.email,
      'Typ av beställning': config.title,
      'Namn / Företag': formData.name,
      'E-post': formData.email,
      Telefon: formData.phone,
      'Önskat datum': formData.date,
      'Önskad tid': formData.time,
      Leveranssätt: formData.deliveryType,
      Ordertyp: formData.orderType,
      Beställningsdetaljer: formData.details
    };
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss på info@topnotchbakery.se');
      }
    } catch {
      setSubmitError('Kunde inte skicka beställningen. Kontrollera din anslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-177">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-178">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-179">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-180" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-2" data-id="element-181">
            Tack för din förfrågan!
          </h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-182">
            Vi har tagit emot din förfrågan gällande{' '}
            <span className="font-semibold text-[#1A1A1A]" data-id="element-183">{config.title}</span>
            . Vi återkommer till dig via e-post eller telefon så snart vi kan
            för att bekräfta detaljerna.
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-184">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={e => e.target === e.currentTarget && onClose()} data-id="element-185">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-186">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-187">
          <div className="flex items-start gap-4" data-id="element-188">
            <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-189">
              {config.emoji}
            </div>
            <div data-id="element-190">
              <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-191">
                {config.title}
              </h3>
              <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-192">
                {config.subtitle}
              </p>
              <p className="text-xs text-[#1A1A1A]/50" data-id="element-193">
                Skickas till info@topnotchbakery.se
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-194">
            <X className="w-6 h-6" data-id="element-195" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8 custom-scrollbar" data-id="element-196">
          <form id="order-form" onSubmit={handleSubmit} className="space-y-8" data-id="element-197">
            {/* Contact Info */}
            <div className="space-y-4" data-id="element-198">
              <h4 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2 border-l-4 border-[#C5A55A] pl-3" data-id="element-199">
                <ShoppingBag className="w-5 h-5 text-[#C5A55A]" data-id="element-200" />
                Kontaktuppgifter
              </h4>

              <div data-id="element-201">
                <label htmlFor="name" className={labelClass} data-id="element-202">
                  Namn / Företagsnamn {required}
                </label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className={inputClass} placeholder="Ditt namn eller företagets namn" data-id="element-203" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-204">
                <div data-id="element-205">
                  <label htmlFor="email" className={labelClass} data-id="element-206">
                    E-post {required}
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder="din@epost.se" data-id="element-207" />
                </div>
                <div data-id="element-208">
                  <label htmlFor="phone" className={labelClass} data-id="element-209">
                    Telefon {required}
                  </label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} placeholder="070-123 45 67" data-id="element-210" />
                </div>
              </div>
            </div>

            {/* Delivery Details */}
            <div className="space-y-4" data-id="element-211">
              <h4 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2 border-l-4 border-[#C5A55A] pl-3" data-id="element-212">
                <Truck className="w-5 h-5 text-[#C5A55A]" data-id="element-213" />
                Leverans & Tid
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-214">
                <div data-id="element-215">
                  <label htmlFor="date" className={labelClass} data-id="element-216">
                    Önskat datum {required}
                  </label>
                  <div className="relative" data-id="element-217">
                    <input type="date" id="date" name="date" required value={formData.date} onChange={handleChange} className={`${inputClass} pl-10`} data-id="element-218" />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-219" />
                  </div>
                </div>
                <div data-id="element-220">
                  <label htmlFor="time" className={labelClass} data-id="element-221">
                    Önskad tid {required}
                  </label>
                  <div className="relative" data-id="element-222">
                    <input type="time" id="time" name="time" required value={formData.time} onChange={handleChange} className={`${inputClass} pl-10`} data-id="element-223" />
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-224" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2" data-id="element-225">
                <div data-id="element-226">
                  <label className={labelClass} data-id="element-227">Leveranssätt {required}</label>
                  <div className="flex gap-4" data-id="element-228">
                    {['Avhämtning', 'Utkörning'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer" data-id="element-229">
                        <input type="radio" name="deliveryType" value={option} checked={formData.deliveryType === option} onChange={handleChange} className="text-[#C5A55A] focus:ring-[#C5A55A]" data-id="element-230" />
                        <span className="text-sm" data-id="element-231">{option}</span>
                      </label>)}
                  </div>
                </div>
                <div data-id="element-232">
                  <label className={labelClass} data-id="element-233">Ordertyp {required}</label>
                  <div className="flex gap-4" data-id="element-234">
                    {['Engångsorder', 'Stående order'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer" data-id="element-235">
                        <input type="radio" name="orderType" value={option} checked={formData.orderType === option} onChange={handleChange} className="text-[#C5A55A] focus:ring-[#C5A55A]" data-id="element-236" />
                        <span className="text-sm" data-id="element-237">{option}</span>
                      </label>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="space-y-4" data-id="element-238">
              <h4 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2 border-l-4 border-[#C5A55A] pl-3" data-id="element-239">
                <ShoppingBag className="w-5 h-5 text-[#C5A55A]" data-id="element-240" />
                Beställning
              </h4>

              <div data-id="element-241">
                <label htmlFor="details" className={labelClass} data-id="element-242">
                  Vad vill du beställa? {required}
                </label>
                <p className="text-xs text-[#1A1A1A]/50 mb-2" data-id="element-243">
                  Beskriv vad du önskar (t.ex. frukostpaket, luncher, tårta,
                  antal personer, allergier etc.)
                </p>
                <textarea id="details" name="details" required value={formData.details} onChange={handleChange} className={textareaClass} placeholder="Beskriv din beställning här..." data-id="element-244" />
              </div>
            </div>

            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-245">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-246" />
                <p data-id="element-247">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-248">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-249">
            Avbryt
          </Button>
          <Button type="submit" form="order-form" isLoading={isSubmitting} className="min-w-[160px]" data-id="element-250">
            <Send className="w-4 h-4 mr-2" data-id="element-251" />
            Skicka förfrågan
          </Button>
        </div>
      </div>
    </div>;
}